import { BadgeElement } from "sharedTypes/badge"
import { getElementAABB } from "components/CanvasEditor/lib/geometry"
import { Rect } from "components/CanvasEditor/lib/viewport"
import { BadgeElementFormValues } from "../Panels/EditorPanel/BadgeSidePanelForm"
import { elementToFormValues } from "./elementTransformers"

export const GEOMETRY = ["x", "y", "width", "height"]

const isObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
)

// Each key with the value every one shares, nested objects key by key; null where they differ
// (not undefined: React Hook Form shows a field's value from when it mounted for undefined)
const common = (values: unknown[]): unknown => {
  if (values.every(isObject)) {
    const keys = new Set(values.flatMap((value) => Object.keys(value)))

    return Object.fromEntries(Array.from(keys).map((key) => [
      key, common(values.map((value) => value[key])),
    ]))
  }
  const [first, ...rest] = values

  return rest.every((value) => JSON.stringify(value) === JSON.stringify(first)) ? first : null
}

// The paths, nested objects key by key, where the values aren't all the same
const differing = (values: unknown[], path = ""): string[] => {
  if (values.every(isObject)) {
    const keys = new Set(values.flatMap((value) => Object.keys(value)))

    return Array.from(keys).flatMap((key) => (
      differing(values.map((value) => value[key]), path ? `${path}.${key}` : key)
    ))
  }
  const [first, ...rest] = values

  return rest.every((value) => JSON.stringify(value) === JSON.stringify(first)) ? [] : [path]
}

/** The fields where elements differ; the group's position and size are never mixed. */
export const mixedFields = (elements: BadgeElement[]) => new Set(
  differing(elements.map(elementToFormValues)).filter((path) => !GEOMETRY.includes(path)),
)

/** The box around elements, rotation included. */
export const groupBox = (elements: BadgeElement[]): Rect => {
  const boxes = elements.map((element) => getElementAABB(element, element.transforms?.rotate || 0))
  const x = Math.min(...boxes.map((box) => box.x))
  const y = Math.min(...boxes.map((box) => box.y))

  return {
    x,
    y,
    width: Math.max(...boxes.map((box) => box.x + box.width)) - x,
    height: Math.max(...boxes.map((box) => box.y + box.height)) - y,
  }
}

const round = (value: number) => Number(value.toFixed(3))

/**
 * The form values several elements have in common, a field null where they differ; the
 * position and size are the group's box.
 */
export const groupFormValues = (elements: BadgeElement[]): BadgeElementFormValues => {
  const box = groupBox(elements)

  return {
    ...common(elements.map(elementToFormValues)) as BadgeElementFormValues,
    x: round(box.x),
    y: round(box.y),
    width: round(box.width),
    height: round(box.height),
  }
}

/** The elements moved and scaled from the group's `box` into `next`. */
export const placeInBox = (elements: BadgeElement[], box: Rect, next: Rect) => {
  const scaleX = box.width > 0 ? next.width / box.width : 1
  const scaleY = box.height > 0 ? next.height / box.height : 1

  return new Map(elements.map((element) => [element.id, {
    x: round(next.x + (element.x - box.x) * scaleX),
    y: round(next.y + (element.y - box.y) * scaleY),
    width: round(element.width * scaleX),
    height: round(element.height * scaleY),
  }]))
}

type Turn = { rotate: number; flipHorizontal: boolean; flipVertical: boolean }

const turnOf = (element: BadgeElement): Turn => element.transforms ?? {
  rotate: 0, flipHorizontal: false, flipVertical: false,
}

const normalise = (degrees: number) => round(((degrees % 360) + 360) % 360)

/** The elements turned together around the centre of their box, as one picture. */
export const rotateGroup = (elements: BadgeElement[], degrees: number) => {
  const box = groupBox(elements)
  const cx = box.x + box.width / 2
  const cy = box.y + box.height / 2
  const radians = (degrees * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  return new Map(elements.map((element) => {
    const px = element.x + element.width / 2 - cx
    const py = element.y + element.height / 2 - cy
    const turn = turnOf(element)

    return [element.id, {
      x: round(cx + px * cos - py * sin - element.width / 2),
      y: round(cy + px * sin + py * cos - element.height / 2),
      transforms: { ...turn, rotate: normalise(turn.rotate + degrees) },
    }]
  }))
}

/** The elements mirrored together across the centre of their box, as one picture. */
export const flipGroup = (elements: BadgeElement[], axis: "horizontal" | "vertical") => {
  const box = groupBox(elements)
  const cx = box.x + box.width / 2
  const cy = box.y + box.height / 2

  return new Map(elements.map((element) => {
    const turn = turnOf(element)
    const mirrored = { ...turn, rotate: normalise(-turn.rotate) }

    return [element.id, axis === "horizontal"
      ? {
        x: round(2 * cx - element.x - element.width),
        transforms: { ...mirrored, flipHorizontal: !turn.flipHorizontal },
      }
      : {
        y: round(2 * cy - element.y - element.height),
        transforms: { ...mirrored, flipVertical: !turn.flipVertical },
      }]
  }))
}
